import { WEEKDAY_KEYS, formatTimeOfDay, parseTimeOfDay, type WeekdayKey } from "./availability-window";
import {
  parseOperatingHours,
  rangesForDay,
  slotWindowMinutes,
  type CanonicalOperatingHours,
  type OperatingHoursRange,
} from "./operating-hours";

import { nounGrammar, type GrammarNoun } from "./tenant-settings/nounGrammar";
import { bookableBlocksMessages } from "./tenant-settings/serverMirrorCopy";
import { vocabulary } from "./tenant-settings/vocabulary";

export type BookableBlocks = CanonicalOperatingHours;

const SHORT_KEYS = new Set<string>(WEEKDAY_KEYS);
const DEFAULT_SLOT_MINUTES = 120;

function isHhMm(value: unknown): value is string {
  return typeof value === "string" && parseTimeOfDay(value) !== null;
}

function rangeFromUnknown(value: unknown): OperatingHoursRange | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const rec = value as Record<string, unknown>;
  if (!isHhMm(rec.open) || !isHhMm(rec.close)) return null;
  return { open: rec.open, close: rec.close };
}

export function parseBookableBlocks(raw: unknown): BookableBlocks {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return {};
  const rec = raw as Record<string, unknown>;
  const out: BookableBlocks = {};
  for (const [key, value] of Object.entries(rec)) {
    if (!SHORT_KEYS.has(key)) continue;
    const day = key as WeekdayKey;
    if (value == null) continue;
    if (!Array.isArray(value)) return {};
    if (value.length === 0) continue;
    const ranges: OperatingHoursRange[] = [];
    for (const item of value) {
      const range = rangeFromUnknown(item);
      if (!range) return {};
      ranges.push(range);
    }
    out[day] = ranges;
  }
  return out;
}

export function serializeBookableBlocks(blocks: BookableBlocks): BookableBlocks | null {
  const out: BookableBlocks = {};
  for (const day of WEEKDAY_KEYS) {
    const ranges = blocks[day] ?? [];
    if (ranges.length === 0) continue;
    out[day] = ranges.map((r) => ({ open: r.open, close: r.close }));
  }
  return Object.keys(out).length === 0 ? null : out;
}

function rangeMinutes(range: OperatingHoursRange): { open: number; close: number } | null {
  const open = parseTimeOfDay(range.open);
  const close = parseTimeOfDay(range.close);
  if (open === null || close === null) return null;
  if (close <= open) return null;
  return { open, close };
}

export function sameDayBlocksOverlap(blocks: BookableBlocks): boolean {
  for (const day of WEEKDAY_KEYS) {
    const ranges = (blocks[day] ?? [])
      .map(rangeMinutes)
      .filter((r): r is { open: number; close: number } => r != null)
      .sort((a, b) => a.open - b.open);
    for (let i = 1; i < ranges.length; i++) {
      if (ranges[i].open < ranges[i - 1].close) return true;
    }
  }
  return false;
}

export function blocksFitHours(blocks: BookableBlocks, hours: CanonicalOperatingHours): boolean {
  for (const day of WEEKDAY_KEYS) {
    const dayBlocks = blocks[day] ?? [];
    if (dayBlocks.length === 0) continue;
    const windows = rangesForDay(hours, day)
      .map(slotWindowMinutes)
      .filter((w): w is { open: number; close: number } => w != null);
    for (const block of dayBlocks) {
      const span = rangeMinutes(block);
      if (!span) return false;
      const fits = windows.some(
        (window) => span.open >= window.open && span.close <= window.close,
      );
      if (!fits) return false;
    }
  }
  return true;
}

export function generateBlocksFromSlice(
  hours: CanonicalOperatingHours,
  slotMinutes: number | null,
): BookableBlocks {
  const duration = slotMinutes && slotMinutes > 0 ? slotMinutes : DEFAULT_SLOT_MINUTES;
  const out: BookableBlocks = {};
  for (const day of WEEKDAY_KEYS) {
    const blocks: OperatingHoursRange[] = [];
    for (const range of rangesForDay(hours, day)) {
      const window = slotWindowMinutes(range);
      if (!window) continue;
      for (let start = window.open; start + duration <= window.close; start += duration) {
        blocks.push({
          open: formatTimeOfDay(start),
          close: formatTimeOfDay(start + duration),
        });
      }
    }
    if (blocks.length > 0) out[day] = blocks;
  }
  return out;
}

export function weekHasBlock(blocks: BookableBlocks): boolean {
  return WEEKDAY_KEYS.some((day) => (blocks[day] ?? []).length > 0);
}

export function isListedBlock(
  blocks: BookableBlocks,
  day: WeekdayKey,
  startMin: number,
  endMin: number,
): boolean {
  return (blocks[day] ?? []).some((range) => {
    const span = rangeMinutes(range);
    return span != null && span.open === startMin && span.close === endMin;
  });
}

export function blockKey(range: OperatingHoursRange): string | null {
  const span = rangeMinutes(range);
  if (!span) return null;
  return `${span.open}-${span.close}`;
}

export function removedOrChangedKeys(
  previous: BookableBlocks,
  next: BookableBlocks,
  day: WeekdayKey,
): Set<string> {
  const before = new Set(
    (previous[day] ?? []).map(blockKey).filter((k): k is string => k != null),
  );
  const after = new Set(
    (next[day] ?? []).map(blockKey).filter((k): k is string => k != null),
  );
  const gone = new Set<string>();
  for (const key of before) {
    if (!after.has(key)) gone.add(key);
  }
  return gone;
}

export function spaceBookableWriteError(args: {
  requiresBooking: boolean;
  hours: CanonicalOperatingHours;
  blocks: BookableBlocks;
  previousBlocks: BookableBlocks;
  futureKeys: { day: WeekdayKey; key: string }[];
  /** KLUB-283 U9 — the club's booking term; the platform default when absent. */
  booking?: GrammarNoun | null;
}): string | null {
  const messages = bookableBlocksMessages(
    nounGrammar(args.booking ?? vocabulary.defaults.booking, vocabulary.defaults.booking.gender),
  );
  if (!args.requiresBooking) {
    if (args.futureKeys.length > 0) {
      return messages.offWithFuture;
    }
    return null;
  }
  if (!weekHasBlock(args.blocks)) {
    return messages.onNeedsBlock;
  }
  if (sameDayBlocksOverlap(args.blocks)) {
    return "Blocos do mesmo dia nao podem se sobrepor.";
  }
  if (!blocksFitHours(args.blocks, args.hours)) {
    return "Cada bloco tem que caber no funcionamento daquele dia.";
  }
  for (const { day, key } of args.futureKeys) {
    if (removedOrChangedKeys(args.previousBlocks, args.blocks, day).has(key)) {
      return messages.changeWithFuture;
    }
  }
  return null;
}

/** Convenience: parse hours dialects then slice. Used by the U2 backfill. */
export function generateBlocksFromHoursRaw(
  rawHours: unknown,
  slotMinutes: number | null,
): BookableBlocks {
  return generateBlocksFromSlice(parseOperatingHours(rawHours), slotMinutes);
}
